<template>
  <div v-hotkey="shortcuts" class="agent">
    <div v-if="isLoading" class="agent-loading">
      <p>Loading agent...</p>
    </div>
    <div v-else-if="agent" class="agent-layout">
      <AgentConversationList
        :conversations="conversations"
        :is-loading="conversationsLoading"
        :active-conversation-id="null"
        @select="onSelectConversation"
        @new-chat="onNewChat"
        @delete="onDeleteConversation"
        @sidebar-resized="(w) => (leftPanelWidth = w)"
      />

      <div class="agent-main">
        <CustomObjectTopBar
          v-if="id && db"
          :object-id="id"
          :database="db"
          :hierarchy="hierarchy"
          :is-agent="true"
          :organization-id="organizationId"
          :left-panel-width="leftPanelWidth"
          :is-navigation-displayed="false"
        >
          <template #left>
            <Breadcrumb :items="agentBreadcrumbItems" :home="breadcrumbHome" />
          </template>
          <template #extra-right>
            <BaseButton
              kind="auxiliary"
              text="Configuration"
              @click="onToggleConfiguration"
            >
              <template #icon>
                <SettingsIcon />
              </template>
            </BaseButton>
          </template>
          <template #more-extra>
            <BaseButton
              class="context-action"
              kind="auxiliary"
              text="Copy Link"
              tint="none"
              is-expanded
              @click="copyAgentLink"
            >
              <template #icon><CopyLinkIcon /></template>
              <template #right>{{ copyLinkShortcutDisplay }}</template>
            </BaseButton>
            <BaseButton
              v-if="agentCatalogKey !== 'assistant'"
              class="context-action"
              kind="auxiliary"
              text="Delete"
              tint="red"
              is-expanded
              @click="deleteAgent"
            >
              <template #icon><DeleteIcon /></template>
            </BaseButton>
          </template>
        </CustomObjectTopBar>
        <BaseTopBar
          v-else-if="isMounted"
          :left-panel-width="leftPanelWidth"
          :is-navigation-displayed="false"
        >
          <template #left>
            <Breadcrumb :items="agentBreadcrumbItems" :home="breadcrumbHome" />
          </template>
          <template #right>
            <BaseButton
              v-if="agentCatalogKey !== 'assistant'"
              kind="auxiliary"
              :text="pinButtonText"
              @click="togglePin"
            >
              <template #icon>
                <PinIcon :pinned="isPinned" />
              </template>
            </BaseButton>
            <BaseButton
              v-if="agentCatalogKey === 'assistant'"
              kind="auxiliary"
              text="Automations"
              @click="onOpenAutomations"
            >
              <template #icon>
                <AutomationIcon />
              </template>
            </BaseButton>
            <ShareButton
              :root="agentShareRoot"
              :organization-id="organizationId"
            />
            <BaseButton
              kind="auxiliary"
              text="Configuration"
              @click="onToggleConfiguration"
            >
              <template #icon>
                <SettingsIcon />
              </template>
            </BaseButton>
          </template>
          <template #more>
            <BaseButton
              class="context-action"
              kind="auxiliary"
              text="Copy Link"
              tint="none"
              is-expanded
              @click="copyAgentLink"
            >
              <template #icon><CopyLinkIcon /></template>
              <template #right>{{ copyLinkShortcutDisplay }}</template>
            </BaseButton>
            <BaseButton
              v-if="agentCatalogKey !== 'assistant'"
              class="context-action"
              kind="auxiliary"
              text="Delete"
              tint="red"
              is-expanded
              @click="deleteAgent"
            >
              <template #icon><DeleteIcon /></template>
            </BaseButton>
          </template>
        </BaseTopBar>

        <!-- Home view -->
        <div class="home-view">
          <!-- Assistant: centered input + prompt chips -->
          <template v-if="agentCatalogKey === 'assistant'">
            <div class="assistant-home">
              <div class="assistant-center">
                <h1 class="assistant-name">{{ agentName }}</h1>
                <p
                  v-if="agentDefinition?.description"
                  class="assistant-tagline"
                >
                  {{ agentDefinition.description }}
                </p>
                <ChatInput
                  ref="chatInput"
                  :workspace-id="workspaceId!"
                  :reference-search-organization-id="
                    referenceSearchOrganizationId
                  "
                  :reference-context-workspace-id="referenceContextWorkspaceId"
                  :integration-organization-ids="integrationOrganizationIds"
                  :mentions-enabled="mentionsEnabled"
                  :attachments="chatAttachments"
                  :uploading-files="uploadingFiles"
                  :submit-button-state="submitButtonState"
                  :error-message="errorMessage"
                  @submit="onSubmit"
                  @paste="onPaste"
                  @file-input-change="onFileInputChange"
                  @drop-files="onDropFiles"
                  @remove-attachment="removeAttachment"
                >
                  <template #placeholder>
                    <AgentPlaceholder :mentions-enabled="mentionsEnabled" />
                  </template>
                </ChatInput>
                <IntegrationPillsBar
                  v-if="workspaceId"
                  class="assistant-integration-pills"
                  :workspace-id="workspaceId"
                  :organization-ids="integrationOrganizationIds"
                  @object-click="onObjectPillClick"
                  @integration-click="onIntegrationPillClick"
                />
                <div class="prompt-examples">
                  <button
                    v-for="prompt in assistantPrompts"
                    :key="prompt"
                    class="prompt-chip"
                    @click="onPromptClick(prompt)"
                  >
                    {{ prompt }}
                  </button>
                </div>
              </div>
            </div>
          </template>

          <!-- Other agents: standard layout -->
          <template v-else>
            <div class="home-header">
              <div class="agent-avatar">
                <img
                  v-if="agentAvatarUrl"
                  :src="agentAvatarUrl"
                  :alt="agentName || 'Agent'"
                />
                <div v-else class="avatar-placeholder">
                  {{ (agentName || "A").charAt(0) }}
                </div>
              </div>
              <!-- Editable in place (like the Automation screen title):
                   typing saves after a debounce, and blurring — clicking
                   outside — flushes the pending save immediately. -->
              <RInput
                ref="nameInput"
                v-model:value="editableName"
                class="agent-name"
                placeholder="Agent"
                :debounce="5000"
                @enter="onNameEnter"
                @blur="onNameBlur"
              />
              <p v-if="agentDefinition?.description" class="agent-tagline">
                {{ agentDefinition.description }}
              </p>
            </div>

            <div class="home-scroll-area">
              <div class="home-content">
                <div class="home-automations">
                  <div class="automations-header">
                    <h4 class="automations-title">Automations</h4>
                    <BaseButton
                      kind="main"
                      type="primary"
                      text="+ New"
                      @click="onCreateAutomation"
                    />
                  </div>
                  <AutomationsList
                    :automations="automations"
                    :organization-id="organizationId"
                    :deleting-automation-id="deletingAutomationId"
                    :disabling-automation-id="disablingAutomationId"
                    :running="runningAutomations"
                    @navigate="navigateToAutomation"
                    @rename="onRenameAutomation"
                    @run="runAutomation"
                    @disable="(id) => onToggleAutomationEnabled(id, false)"
                    @enable="(id) => onToggleAutomationEnabled(id, true)"
                    @delete="deleteAutomation"
                  />
                </div>
              </div>
            </div>

            <div class="home-input-wrapper">
              <ChatInput
                ref="chatInput"
                :workspace-id="workspaceId!"
                :reference-search-organization-id="
                  referenceSearchOrganizationId
                "
                :reference-context-workspace-id="referenceContextWorkspaceId"
                :integration-organization-ids="integrationOrganizationIds"
                :mentions-enabled="mentionsEnabled"
                :attachments="chatAttachments"
                :uploading-files="uploadingFiles"
                :submit-button-state="submitButtonState"
                :error-message="errorMessage"
                @submit="onSubmit"
                @paste="onPaste"
                @file-input-change="onFileInputChange"
                @drop-files="onDropFiles"
                @remove-attachment="removeAttachment"
              >
                <template #placeholder>
                  <AgentPlaceholder :mentions-enabled="mentionsEnabled" />
                </template>
              </ChatInput>
              <IntegrationPillsBar
                v-if="workspaceId"
                :workspace-id="workspaceId"
                :organization-ids="integrationOrganizationIds"
                @object-click="onObjectPillClick"
                @integration-click="onIntegrationPillClick"
              />
            </div>
          </template>
        </div>
      </div>
    </div>
    <div v-else class="agent-not-found">
      <p>Agent not found or not enabled.</p>
    </div>
  </div>
</template>

<script lang="ts">
import type { Node as ProseMirrorNode } from "prosemirror-model";
import {
  computed,
  defineComponent,
  h,
  nextTick,
  onMounted,
  ref,
  toRefs,
  watch,
} from "vue";
import { useModal } from "vue-final-modal";
import { onBeforeRouteLeave } from "vue-router";

import type {
  AnyAgentId,
  AutomationId,
  OrgId,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import { copy } from "@/clipboard";
import { getAvatarUrlByKey } from "@/components/ai/AgentAvatar.vue";
import AgentConversationList from "@/components/ai/AgentConversationList.vue";
import AgentPlaceholder from "@/components/ai/AgentPlaceholder.vue";
import AutomationsList from "@/components/ai/AutomationsList.vue";
import ChatInput, { type ChatInputHandle } from "@/components/ai/ChatInput.vue";
import { insertIntegrationPill } from "@/components/ai/insertIntegrationPill";
import { getAgentIntegrationOrganizationIds } from "@/components/ai/integrationOrganizations";
import IntegrationPillsBar, {
  type IntegrationPill,
} from "@/components/ai/IntegrationPillsBar.vue";
import NewAutomationModalBody from "@/components/ai/NewAutomationModalBody.vue";
import { pendingMessage } from "@/components/ai/pendingMessage";
import { useAgent } from "@/components/ai/useAgent";
import { getCustomAgentOverride } from "@/components/ai/useAgents";
import { useAttachments } from "@/components/ai/useAttachments";
import { useConversations } from "@/components/ai/useConversations";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseTopBar from "@/components/base/BaseTopBar.vue";
import Breadcrumb, {
  type BreadcrumbHome,
  type BreadcrumbItem,
} from "@/components/Breadcrumb.vue";
import { parseMarkupToDoc } from "@/components/editor/serialize";
import {
  runGraphCommand,
  unwrapGraphCommandResult,
} from "@/components/graph/command";
import CustomObjectTopBar from "@/components/graph/CustomObjectTopBar.vue";
import AutomationIcon from "@/components/icons/AutomationIcon.vue";
import CopyLinkIcon from "@/components/icons/CopyLinkIcon.vue";
import DeleteIcon from "@/components/icons/DeleteIcon.vue";
import PinIcon from "@/components/icons/PinIcon.vue";
import * as inject from "@/components/inject";
import type { ExportRoot } from "@/components/marketplace/export";
import Modal from "@/components/modals/Modal.vue";
import { openUpgradeModal } from "@/components/modals/upgrade";
import RInput from "@/components/RInput.vue";
import ShareButton from "@/components/ShareButton.vue";
import {
  assignShortcuts,
  copyLinkItem,
  Shortcuts,
} from "@/components/shortcuts";
import templates, { instantiate } from "@/components/templates";
import { getOrganizationDefaultWorkspace } from "@/components/workspaces";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import { RouteNames } from "@/routes/types";
import { waitForEntitlement } from "@/utils/billing";
import { hotkeys } from "@/utils/hotkeys";
import { keymap } from "@/utils/keymap";
import {
  navigateTo,
  navigateToAgentConfiguration,
  navigateToAgentConversation,
  navigateToAutomation as navigateToAutomation_,
} from "@/utils/navigate";

import SettingsIcon from "../icons/tabs/SettingsIcon.vue";

const ASSISTANT_PROMPTS = [
  "What's on my plate today?",
  "Block time for my tasks of the day",
  "Book a meeting with my team",
  "Cancel my afternoon meetings",
] as const;

export default defineComponent({
  name: "Agent",
  components: {
    AgentConversationList,
    AgentPlaceholder,
    AutomationIcon,
    AutomationsList,
    BaseButton,
    BaseTopBar,
    Breadcrumb,
    ChatInput,
    CopyLinkIcon,
    CustomObjectTopBar,
    DeleteIcon,
    IntegrationPillsBar,
    PinIcon,
    RInput,
    SettingsIcon,
    ShareButton,
  },
  props: {
    organizationId: { type: String, required: true },
    agentId: { type: String, required: true },
  },
  async setup(props) {
    const { organizationId, agentId } = toRefs(props);

    const controller = inject.controller();
    const billing = inject.billing();
    const router = inject.router();
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();
    const profile = inject.profile();
    const menu = inject.menu();
    const logger = inject.logger("Agent");
    const baseLogger = logger.base();
    const agents = inject.agents();
    const automations = inject.automations();

    const leftPanelWidth = ref(252);
    const isMounted = ref(false);

    onMounted(() => {
      isMounted.value = true;
    });

    const releaser = useReleaser();

    // Flush the agent editors' debounced edits when leaving the page, so the
    // controller has the latest content before the next mount re-fetches the
    // agent. The guard must be registered before the first `await` (it
    // attaches to the active component instance), so the flush is late-bound
    // once the agent loads below.
    let flushAgentEdits: (() => void) | undefined;
    onBeforeRouteLeave(() => flushAgentEdits?.());

    const missingValues =
      !organizationId.value || !agentId.value || !profile.value;
    if (missingValues) {
      logger.debug("Agent", "Missing required values", {
        organizationId: !!organizationId.value,
        agentId: !!agentId.value,
        profile: !!profile.value,
      });
      return {
        workspaceId: ref(""),
        isMounted: ref(false),
        leftPanelWidth,
        agent: ref(undefined),
        agentCatalogKey: ref(undefined),
        agentDefinition: ref(undefined),
        agentName: ref("Agent"),
        editableName: ref(""),
        nameInput: ref(null),
        onNameEnter: () => {},
        onNameBlur: () => {},
        agentBreadcrumbItems: computed<BreadcrumbItem[]>(() => []),
        breadcrumbHome: computed((): BreadcrumbHome | undefined => undefined),
        hierarchy: ref([]),
        db: ref(undefined),
        id: ref(undefined),
        isLoading: ref(true),
        conversations: ref([]),
        conversationsLoading: ref(true),
        isThinking: ref(false),
        errorMessage: ref<string | null>(null),
        mentionsEnabled: ref(false),
        referenceSearchOrganizationId: ref<OrgId | null>(null),
        referenceContextWorkspaceId: ref<WorkspaceId | null>(null),
        integrationOrganizationIds: ref<OrgId[] | undefined>(undefined),
        submitButtonState: ref("default"),
        chatInput: ref(null),
        chatAttachments: ref([]),
        uploadingFiles: ref([]),
        removeAttachment: () => {},
        onPaste: () => {},
        onFileInputChange: () => {},
        onSubmit: () => {},
        onPromptClick: () => {},
        assistantPrompts: ASSISTANT_PROMPTS,
        onSelectConversation: () => {},
        onNewChat: () => {},
        onDeleteConversation: () => {},
        onToggleConfiguration: () => {},
        onOpenAutomations: () => {},
        onCreateAutomation: () => {},
        onCreateOrphanAutomation: () => {},
        automations: ref([]),
        disablingAutomationId: ref(null),
        onDisableAutomation: () => {},
        onEnableAutomation: () => {},
        runAutomation: () => {},
        onRenameAutomation: () => {},
        onDropFiles: () => {},
      };
    }

    const result = await releaser.add(
      useAgent(
        controller,
        profile,
        baseLogger,
        organizationId,
        agentId,
        undefined,
        "Agent"
      )
    );
    flushAgentEdits = result.flushPendingEdits;

    const {
      agent,
      hierarchy,
      db,
      id,
      isLoading: isAgentLoading,
      conversationIds,
    } = result;

    const workspaceId = await releaser.add(
      bindAsync(organizationId, async (orgId) => {
        if (!orgId) return releasableRef<WorkspaceId | undefined>(undefined);
        const wsId = await getOrganizationDefaultWorkspace(controller, orgId);
        return releasableRef<WorkspaceId | undefined>(wsId);
      })
    );

    const {
      attachments: chatAttachments,
      uploading: uploadingFiles,
      addFiles,
      remove: removeAttachment,
      clear: clearAttachments,
    } = useAttachments(controller, workspaceId);

    const onDropFiles = (files: File[]) => {
      if (files.length > 0) void addFiles(files);
    };

    const onFileInputChange = (event: Event) => {
      if (!(event.target instanceof HTMLInputElement)) return;
      const input = event.target;
      if (input.files && input.files.length > 0) {
        void addFiles(Array.from(input.files));
        input.value = "";
      }
    };

    const onPaste = (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items) return;
      const files: File[] = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === "file") {
          const file = items[i].getAsFile();
          if (file) files.push(file);
        }
      }
      if (files.length > 0) {
        event.preventDefault();
        void addFiles(files);
      }
    };

    const agentCatalogKey = computed(() => agent.value?.key);
    const agentDefinition = computed(() => {
      const key = agentCatalogKey.value;
      if (!key) return undefined;
      return agents.value?.catalog?.[key];
    });
    const agentName = computed(() => {
      const override = getCustomAgentOverride(agentId.value);
      return (
        agent.value?.name ??
        agentDefinition.value?.name ??
        override?.name ??
        "Agent"
      );
    });
    const agentAvatarUrl = computed(() => {
      const catalogUrl = agentDefinition.value?.avatar;
      if (catalogUrl) return catalogUrl;
      const override = getCustomAgentOverride(agentId.value);
      return override?.avatar ? getAvatarUrlByKey(override.avatar) : "";
    });

    // ── Name (edited in place) ───────────────────────────────────────

    // Renaming goes through the graph, which exposes a writable "Name" field
    // on agents (the dedicated `controller.ai.agent.update` patch has no
    // name). Builtin objects are addressed by their top-level binding —
    // `ai_agents[id]`, like `tasks[id]` in applyBoardDrop — never by
    // `databases.[id].rows[...]`, which resolves to the raw extension table
    // (custom fields only, no "Name").
    const saveName = async (name: string) => {
      const wsId = workspaceId.value;
      if (!wsId) throw new Error("no workspace for agent");
      const command = unwrapGraphCommandResult(
        await controller.graph.command.compile(
          wsId,
          `ai_agents[targetId] set [propertyTitle] = newValue`,
          { targetId: agentId.value, propertyTitle: "Name", newValue: name }
        ),
        "compile"
      );
      await runGraphCommand(controller, command);
    };

    // Local copy so a pending debounced save doesn't get clobbered when the
    // reactive agent data refreshes (same pattern as Automation.vue).
    const localName = ref("");
    releaser.defer(
      watch(
        agentName,
        (name) => {
          localName.value = name;
        },
        { immediate: true }
      )
    );

    const editableName = computed({
      get: () => localName.value,
      set: (value: string | undefined) => {
        localName.value = value ?? "";
        const name = (value ?? "").trim();
        // Never persist an empty name; onNameBlur restores the saved one.
        if (!name || name === agentName.value) return;
        saveName(name).catch((e: unknown) => {
          logger.error("Failed to rename agent", { error: e });
          // Drop the failed value: leaving it displayed would let the user
          // believe the rename took.
          localName.value = agentName.value;
        });
      },
    });

    const nameInput = ref<InstanceType<typeof RInput>>();
    // Enter commits: blurring flushes the debounced save and drops the caret.
    const onNameEnter = () => nameInput.value?.blur();
    // Blurring an emptied name restores the saved one (it is never saved).
    const onNameBlur = () => {
      if (!localName.value.trim()) localName.value = agentName.value;
    };

    const breadcrumbHome = computed((): BreadcrumbHome | undefined => {
      if (agentCatalogKey.value === "assistant") return undefined;
      const oid: OrgId = organizationId.value;
      const personalOid: OrgId | undefined =
        profile.value?.user.personal.organizationId;
      if (personalOid && oid !== personalOid) {
        return { type: "organization", id: oid };
      }
      const wsId = db.value?.workspace ?? workspaceId.value;
      if (wsId) return { type: "workspace", id: wsId };
      return undefined;
    });

    const agentBreadcrumbItems = computed<BreadcrumbItem[]>(() => {
      if (agentCatalogKey.value === "assistant") return [];
      return [
        ...hierarchy.value,
        {
          title: "Agents",
          link: {
            name: RouteNames.SettingsAgents,
            params: { orgId: organizationId.value },
          },
        },
        { title: agentName.value, link: { params: {} } },
      ];
    });

    const navigateToAutomation = async (automationId: AutomationId) => {
      await navigateToAutomation_(router, automationId, organizationId.value);
    };

    const deletingAutomationId = ref<AutomationId | null>(null);
    const deleteAutomation = async (automationId: AutomationId) => {
      if (deletingAutomationId.value) return;

      deletingAutomationId.value = automationId;
      try {
        const orgId = organizationId.value;
        if (!orgId) return;
        await controller.ai.automation.delete(orgId, automationId);
      } finally {
        deletingAutomationId.value = null;
      }
    };

    const disablingAutomationId = ref<AutomationId | null>(null);

    const onToggleAutomationEnabled = async (
      automationId: AutomationId,
      enabled: boolean
    ) => {
      if (disablingAutomationId.value) return;
      disablingAutomationId.value = automationId;
      const orgId = organizationId.value;
      const agentIdValue = agent.value?.id ?? agentId.value;
      if (!orgId || !agentIdValue) {
        disablingAutomationId.value = null;
        return;
      }
      try {
        await controller.ai.automation.update(orgId, automationId, { enabled });
      } finally {
        disablingAutomationId.value = null;
      }
    };

    const runAutomation = (automationId: AutomationId) => {
      const orgId = organizationId.value;
      const agentIdValue = agent.value?.id ?? agentId.value;
      if (!orgId || !agentIdValue) return;
      void automations.value?.run(orgId, agentIdValue, automationId, undefined);
    };

    const newAutomationName = ref("");
    // Target agent for the next "Create" submit. `null` means an orphan
    // (agentless) automation; defaults to the current agent for the existing
    // sidebar entry point.
    const createAutomationTargetAgentId = ref<AnyAgentId | null>(null);
    const createAutomationButtonState = computed(() => {
      if (!newAutomationName.value.trim()) return "disabled";
      return "default";
    });

    const createAutomation = async (): Promise<boolean> => {
      const name = newAutomationName.value.trim();

      if (!name) {
        logger.error("Agent", "Automation name is required");
        return false;
      }

      try {
        const orgId = organizationId.value;
        if (!orgId) return false;

        const firstStepId = await controller.ai.step.create(
          orgId,
          {
            name: "Step 1",
            prompt: {
              context: {},
              document: { blocks: instantiate(templates.empty) },
            },
          },
          null
        );
        const result = await controller.ai.automation.create(
          orgId,
          name,
          [firstStepId],
          createAutomationTargetAgentId.value
        );
        await navigateToAutomation(result);
        return true;
      } catch (e) {
        logger.error("Agent", "Failed to create automation", { error: e });
        return false;
      }
    };

    const { open: openNewAutomationModal, close: closeNewAutomationModal } =
      useModal({
        component: Modal,
        attrs: {
          title: "New automation",
          showTitleBar: true,
          showTopBar: false,
          modalButtons: [
            {
              title: "Create",
              kind: "main",
              state: createAutomationButtonState,
              func: createAutomation,
            },
          ],
        },
        slots: {
          default: () =>
            h(NewAutomationModalBody, {
              modelValue: newAutomationName.value,
              "onUpdate:modelValue": (value: string) => {
                newAutomationName.value = value;
              },
              onSubmit: async () => {
                if (newAutomationName.value.trim()) {
                  const ok = await createAutomation();
                  if (ok) closeNewAutomationModal();
                }
              },
            }),
        },
      });

    const onCreateAutomation = () => {
      if (!agentId.value) {
        logger.error("Agent", "Agent ID is missing");
        return;
      }
      newAutomationName.value = "";
      createAutomationTargetAgentId.value = agentId.value;
      openNewAutomationModal();
    };

    const onCreateOrphanAutomation = () => {
      newAutomationName.value = "";
      createAutomationTargetAgentId.value = null;
      openNewAutomationModal();
    };

    const renamingAutomationId = ref<AutomationId | null>(null);
    const renameAutomationName = ref("");
    const renameAutomationButtonState = computed(() => {
      if (!renameAutomationName.value.trim()) return "disabled";
      return "default";
    });

    const submitRenameAutomation = async (): Promise<boolean> => {
      const name = renameAutomationName.value.trim();
      const automationId = renamingAutomationId.value;
      if (!name || !automationId) return false;
      const orgId = organizationId.value;
      if (!orgId) return false;
      try {
        await controller.ai.automation.update(orgId, automationId, { name });
        return true;
      } catch (e) {
        logger.error("Agent", "Failed to rename automation", { error: e });
        return false;
      }
    };

    const {
      open: openRenameAutomationModal,
      close: closeRenameAutomationModal,
    } = useModal({
      component: Modal,
      attrs: {
        title: "Rename automation",
        showTitleBar: true,
        showTopBar: false,
        modalButtons: [
          {
            title: "Rename",
            kind: "main",
            state: renameAutomationButtonState,
            func: submitRenameAutomation,
          },
        ],
      },
      slots: {
        default: () =>
          h(NewAutomationModalBody, {
            modelValue: renameAutomationName.value,
            "onUpdate:modelValue": (value: string) => {
              renameAutomationName.value = value;
            },
            onSubmit: async () => {
              if (renameAutomationName.value.trim()) {
                const ok = await submitRenameAutomation();
                if (ok) closeRenameAutomationModal();
              }
            },
          }),
      },
    });

    const onRenameAutomation = (
      automationId: AutomationId,
      currentName: string
    ) => {
      renamingAutomationId.value = automationId;
      renameAutomationName.value = currentName;
      openRenameAutomationModal();
    };

    const chatInput = ref<ChatInputHandle | null>(null);

    const { conversations, isLoading: conversationsLoading } = releaser.add(
      useConversations(controller, organizationId, conversationIds)
    );

    const isLoading = computed(() => isAgentLoading.value);

    const mentionsEnabled = computed(() => {
      if (!workspaceId.value) return false;
      if (!profile.value.user.personal.workspaceId) return false;
      return workspaceId.value! !== profile.value.user.personal.workspaceId!;
    });
    const isAssistantAgent = computed(
      () => agentCatalogKey.value === "assistant"
    );
    const referenceSearchOrganizationId = computed<OrgId | null>(() =>
      isAssistantAgent.value ? null : organizationId.value
    );
    const referenceContextWorkspaceId = computed<WorkspaceId | null>(() =>
      isAssistantAgent.value
        ? (profile.value.user.personal.workspaceId ?? null)
        : (workspaceId.value ?? null)
    );
    const integrationOrganizationIds = computed<OrgId[] | undefined>(() => {
      return getAgentIntegrationOrganizationIds(
        profile.value,
        isAssistantAgent.value
      );
    });

    const isThinking = ref(false);
    const errorMessage = ref<string | null>(null);

    const submitButtonState = computed(() =>
      isThinking.value ? "loading" : "default"
    );

    const onSubmit = async (proseMirrorDoc: ProseMirrorNode) => {
      if (!proseMirrorDoc || isThinking.value) return;
      chatInput.value?.setValue?.("");

      const { serializeDoc } = await import("@/components/editor/serialize");

      const currentAttachments =
        chatAttachments.value.length > 0
          ? [...chatAttachments.value]
          : undefined;
      clearAttachments();

      isThinking.value = true;
      errorMessage.value = null;
      try {
        const newConvId = await controller.ai.agent.conversation.create(
          organizationId.value,
          agentId.value,
          null
        );

        pendingMessage.value = {
          conversationId: newConvId,
          doc: proseMirrorDoc,
          text: serializeDoc(proseMirrorDoc),
          attachments: currentAttachments,
        };

        await navigateToAgentConversation(router, agentId.value, newConvId);
      } catch (err) {
        errorMessage.value =
          err instanceof Error ? err.message : "Unable to reach AI model.";
      } finally {
        isThinking.value = false;
      }
    };

    const onSelectConversation = async (id: string) => {
      await navigateToAgentConversation(router, agentId.value, id);
    };

    const onNewChat = () => {
      focusChatComposer();
    };

    const onDeleteConversation = async (id: string) => {
      try {
        await controller.ai.agent.conversation.delete(organizationId.value, id);
      } catch (err) {
        logger.error("Failed to delete conversation", err);
      }
    };

    const focusChatComposer = async () => {
      await nextTick();
      chatInput.value?.focusEditor?.();
      requestAnimationFrame(() => {
        chatInput.value?.focusEditor?.();
      });
    };
    releaser.defer(
      watch(
        () => chatInput.value,
        () => {
          focusChatComposer();
        },
        { immediate: true, flush: "post" }
      )
    );

    const onPromptClick = (prompt: string) => {
      const doc = parseMarkupToDoc(prompt, baseLogger);
      void onSubmit(doc);
    };

    const onToggleConfiguration = async () => {
      await navigateToAgentConfiguration(router, agentId.value);
    };

    const agentShareRoot = computed<ExportRoot>(() => ({
      kind: "agent",
      agentId: agentId.value,
      organizationId: organizationId.value,
    }));

    const copyLinkShortcutDisplay =
      hotkeys.format.stringify(
        [keymap.keyMod, keymap.keyShift, keymap.keyL],
        keymap.formatSymbol,
        keymap.separatorSpace
      ) ?? "";

    const copyAgentLink = async () => {
      const url = `https://open.routine.co?path=/agents/${agentId.value}`;
      await copy(url);
      controller.notifications.notify("Link copied to clipboard");
    };

    const shortcuts = computed<Shortcuts>(() =>
      assignShortcuts(
        { isModalShown, isInputFocused },
        copyLinkItem,
        () => void copyAgentLink()
      )
    );

    const deleteAgent = async () => {
      // The personal assistant is not a deletable agent instance.
      if (agentCatalogKey.value === "assistant") return;
      await agents.value?.destroy(organizationId.value, agentId.value);
      await navigateTo(RouteNames.SettingsAgents, router, {
        params: { orgId: organizationId.value },
      });
    };

    const onOpenAutomations = async () => {
      const orgId = organizationId.value;
      if (!orgId) return;
      // Account-level: any org's plan grants automations.
      const entitled = await waitForEntitlement(billing, "ai-automations");
      if (!entitled) {
        openUpgradeModal(billing, undefined, "ai-automations");
        return;
      }
      await navigateTo(RouteNames.SettingsAutomations, router, {
        params: { orgId },
      });
    };

    const isPinned = computed(() => menu.isItemPinned(agentId.value));
    const pinButtonText = computed(() => (isPinned.value ? "Unpin" : "Pin"));
    const togglePin = async () => {
      if (isPinned.value) {
        const item = menu.visibleItems.value.find(
          (i) =>
            i.target.kind === "agent" && i.target.agent_id === agentId.value
        );
        if (item) await menu.unpinAgent(item);
      } else {
        await menu.pinAgent(agentId.value, organizationId.value);
      }
    };

    const orgAutomations = computed<
      Array<{ id: AutomationId; name: string; agentAvatar?: string }>
    >(() => {
      const orgId = organizationId.value;
      const agentIdValue = agent.value?.id ?? agentId.value;
      const allValue = automations.value?.all.value;
      if (!orgId || !agentIdValue) return [];

      const byOrg = allValue?.[orgId];
      const byAgent = byOrg?.[agentIdValue];
      if (!byAgent) return [];

      const avatar = agentDefinition.value?.avatar || undefined;
      return Object.values(byAgent).map((a) => ({
        id: a.id,
        name: a.name,
        agentAvatar: avatar,
        trigger: a.trigger,
        stepCount: a.stepCount,
        enabled: a.enabled,
      }));
    });

    return {
      agent,
      agentCatalogKey,
      agentDefinition,
      agentName,
      editableName,
      nameInput,
      onNameEnter,
      onNameBlur,
      agentAvatarUrl,
      agentBreadcrumbItems,
      breadcrumbHome,
      workspaceId,
      hierarchy,
      db,
      id,
      isMounted,
      isLoading,
      leftPanelWidth,
      onToggleConfiguration,
      onOpenAutomations,
      agentShareRoot,
      copyAgentLink,
      deleteAgent,
      shortcuts,
      copyLinkShortcutDisplay,
      onCreateAutomation,
      onCreateOrphanAutomation,
      navigateToAutomation,
      deleteAutomation,
      deletingAutomationId,
      disablingAutomationId,
      onToggleAutomationEnabled,
      runAutomation,
      conversations,
      conversationsLoading,
      onSubmit,
      onPromptClick,
      assistantPrompts: ASSISTANT_PROMPTS,
      chatInput,
      onObjectPillClick: () => {
        chatInput.value?.linkObject?.();
      },
      onIntegrationPillClick: (pill: IntegrationPill) => {
        insertIntegrationPill(chatInput.value, pill);
      },
      chatAttachments,
      uploadingFiles,
      removeAttachment,
      onPaste,
      onFileInputChange,
      isThinking,
      errorMessage,
      mentionsEnabled,
      referenceSearchOrganizationId,
      referenceContextWorkspaceId,
      integrationOrganizationIds,
      submitButtonState,
      onSelectConversation,
      onNewChat,
      onDeleteConversation,
      automations: orgAutomations,
      runningAutomations: computed(
        () => automations.value?.running.value ?? []
      ),
      onRenameAutomation,
      isPinned,
      pinButtonText,
      togglePin,
      onDropFiles,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/agent" as *;
@use "@/styles/context-actions.scss";

.agent {
  @include agent-root;

  .agent-layout {
    @include agent-sidebar-layout;
  }

  .agent-main {
    @include agent-main-pane;
  }

  .home-view {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .assistant-home {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow-y: auto;
      padding: 24px;
    }

    .assistant-center {
      width: 100%;
      max-width: 560px;
      display: flex;
      flex-direction: column;
      align-items: center;

      :deep(.chat-input-container) {
        width: 100%;
      }

      :deep(.composer) {
        min-height: 70px;
      }

      .assistant-integration-pills {
        width: 100%;
      }
    }

    .assistant-tagline {
      display: none;
    }

    .prompt-examples {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      justify-content: center;
      margin-top: 32px;
    }

    .prompt-chip {
      padding: 6px 14px;
      border-radius: 20px;
      @extend %font-body-regular;
      transition: background-color 120ms ease-out;
      @include themed() {
        background: t($bg-alt);
        color: t($primary);
        border: 1px solid t($grey-10);
      }

      &:hover {
        @include themed() {
          background: t($grey-10);
        }
      }
    }

    .home-header {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 16px 24px 0 28px;
      gap: 4px;
      flex-shrink: 0;

      .agent-avatar {
        width: 96px;
        height: 96px;
        border-radius: 50%;
        overflow: hidden;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 4px;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .avatar-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          @extend %font-h2;
          font-weight: 500;

          @include themed() {
            background-color: t($tertiary-50);
            color: t($bg);
          }
        }
      }

      .agent-name {
        margin: 0;
        max-width: fit-content;
        // Keep a comfortable click target when the name is emptied (the
        // placeholder is absolutely positioned and adds no width).
        min-width: 200px;
        text-align: center;

        @include themed() {
          color: t($primary);
        }

        :deep(.input),
        :deep(.placeholder) {
          @extend %font-h1;
        }

        // Center the absolutely-positioned placeholder over the input box
        // (it is anchored top-left by default, see RInput.vue).
        :deep(.placeholder) {
          inset-inline-start: 0;
          width: 100%;
        }
      }

      .agent-tagline {
        margin: 0;
        @extend %font-body-regular;
        @include themed() {
          color: t($grey-60);
        }
      }
    }

    .home-scroll-area {
      flex: 1;
      overflow-y: auto;

      .home-content {
        @include agent-content-column;
        padding-top: 8px;
        padding-bottom: 24px;

        .home-automations {
          margin-top: 16px;
          display: flex;
          flex-direction: column;
          row-gap: 12px;
        }
      }
    }

    .home-input-wrapper {
      @include agent-content-column;
      padding-top: 16px;
      padding-bottom: 24px;
      flex-shrink: 0;
    }
  }

  // ── Automations ────────────────────────────────────────────────────────────

  .automations-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .automations-title {
    margin: 0;
    @extend %font-h3;
    @include themed() {
      color: t($primary);
    }
  }
}

.agent-not-found,
.agent-loading {
  @include agent-state-panel;
}
</style>
